import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Settings as SettingsIcon, ChevronDown } from 'lucide-react';
import apiClient from '../../api/client';

const CustomFieldsTab = ({ workspace }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/custom-fields', {
        params: { active_only: true }
      });
      setFields(response.data || []);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      alert('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateField = () => {
    setEditingField(null);
    setShowCreateDialog(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setShowCreateDialog(true);
  };

  const handleDeleteField = async (fieldId) => {
    if (!confirm('Are you sure you want to delete this custom field? All associated data will be lost.')) {
      return;
    }

    try {
      await apiClient.delete(`/api/v1/custom-fields/${fieldId}`);
      alert('Custom field deleted successfully');
      loadFields();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      alert(error.response?.data?.detail || 'Failed to delete custom field');
    }
  };

  const handleSaveField = async (fieldData) => {
    try {
      if (editingField) {
        // Update existing field
        await apiClient.put(`/api/v1/custom-fields/${editingField.id}`, fieldData);
        alert('Custom field updated successfully');
      } else {
        // Create new field
        await apiClient.post('/api/v1/custom-fields', fieldData);
        alert('Custom field created successfully');
      }
      setShowCreateDialog(false);
      loadFields();
    } catch (error) {
      console.error('Error saving custom field:', error);
      alert(error.response?.data?.detail || 'Failed to save custom field');
    }
  };

  const filteredFields = filter === 'all'
    ? fields
    : fields.filter(f => f.entity_type === filter || (filter === 'part' && f.entity_type === 'both') || (filter === 'pipeline' && ['both', 'opportunity'].includes(f.entity_type)));

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text',
      number: 'Number',
      date: 'Date',
      dropdown: 'Dropdown',
      multi_select: 'Multi-Select',
      checkbox: 'Checkbox',
      url: 'URL',
      email: 'Email'
    };
    return labels[type] || type;
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      part: 'Parts Only',
      program: 'Programs Only',
      pipeline: 'Pipeline Only',
      claim: 'Claims Only',
      opportunity: 'Pipeline Only (Legacy)',
      both: 'Parts & Pipeline'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading custom fields...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Configurable Fields</h2>
          <p className="text-sm text-gray-600 mt-1">
            Define workspace-specific fields for programs, parts, pipeline records, and claims
          </p>
        </div>
        <button
          onClick={handleCreateField}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Configurable Field
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'all', label: 'All Fields' },
          { id: 'program', label: 'Programs' },
          { id: 'part', label: 'Parts' },
          { id: 'pipeline', label: 'Pipeline' },
          { id: 'claim', label: 'Claims' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Fields List */}
      {filteredFields.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <SettingsIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No custom fields defined yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Create configurable fields to capture customer-specific data without changing the database schema
          </p>
          <button
            onClick={handleCreateField}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Your First Configurable Field
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFields.map((field) => (
            <div
              key={field.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{field.name}</h3>
                    {field.is_required && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Required</span>
                    )}
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      {getFieldTypeLabel(field.field_type)}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      {getEntityTypeLabel(field.entity_type)}
                    </span>
                  </div>
                  {field.description && (
                    <p className="text-sm text-gray-600 mb-2">{field.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>Key: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{field.field_key}</code></span>
                    {field.options?.values && (
                      <span>
                        Options: {field.options.values.slice(0, 3).join(', ')}
                        {field.options.values.length > 3 && ` +${field.options.values.length - 3} more`}
                      </span>
                    )}
                    {field.default_value && (
                      <span>Default: {field.default_value}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleEditField(field)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit field"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteField(field.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete field"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      {showCreateDialog && (
        <CustomFieldDialog
          field={editingField}
          onClose={() => setShowCreateDialog(false)}
          onSave={handleSaveField}
        />
      )}
    </div>
  );
};

// Dialog Component for Creating/Editing Custom Fields
const CustomFieldDialog = ({ field, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: field?.name || '',
    description: field?.description || '',
    field_type: field?.field_type || 'text',
    entity_type: field?.entity_type || 'both',
    options: field?.options || null,
    default_value: field?.default_value || '',
    is_required: field?.is_required || false,
    display_order: field?.display_order || 0
  });

  const [optionValues, setOptionValues] = useState(
    field?.options?.values?.join('\n') || ''
  );

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'multi_select', label: 'Multi-Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'url', label: 'URL' },
    { value: 'email', label: 'Email' }
  ];

  const entityTypes = [
    { value: 'program', label: 'Programs Only' },
    { value: 'part', label: 'Parts Only' },
    { value: 'pipeline', label: 'Pipeline Only' },
    { value: 'claim', label: 'Claims Only' },
    { value: 'both', label: 'Parts & Pipeline' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build payload
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      field_type: formData.field_type,
      entity_type: formData.entity_type,
      default_value: formData.default_value || undefined,
      is_required: formData.is_required,
      display_order: formData.display_order
    };

    // Add options for dropdown/multi-select
    if (['dropdown', 'multi_select'].includes(formData.field_type)) {
      const values = optionValues
        .split('\n')
        .map(v => v.trim())
        .filter(v => v.length > 0);
      
      if (values.length === 0) {
        alert('Please provide at least one option for dropdown/multi-select fields');
        return;
      }
      payload.options = { values };
    }

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {field ? 'Edit Configurable Field' : 'Create Configurable Field'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Field Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Customer Code, Priority Level"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Optional description for this field"
              />
            </div>

            {/* Field Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Type <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.field_type}
                onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={!!field} // Cannot change type after creation
              >
                {fieldTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {field && (
                <p className="text-xs text-gray-500 mt-1">Field type cannot be changed after creation</p>
              )}
            </div>

            {/* Entity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apply To <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.entity_type}
                onChange={(e) => setFormData({ ...formData, entity_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {entityTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Options (for dropdown/multi-select) */}
            {['dropdown', 'multi_select'].includes(formData.field_type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Options <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={optionValues}
                  onChange={(e) => setOptionValues(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="Enter one option per line&#10;e.g.,&#10;High&#10;Medium&#10;Low"
                />
                <p className="text-xs text-gray-500 mt-1">Enter one option per line</p>
              </div>
            )}

            {/* Default Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Value
              </label>
              {formData.field_type === 'checkbox' ? (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.default_value === 'true'}
                    onChange={(e) => setFormData({ ...formData, default_value: e.target.checked ? 'true' : 'false' })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm text-gray-700">Checked by default</label>
                </div>
              ) : (
                <input
                  type={formData.field_type === 'number' ? 'number' : formData.field_type === 'date' ? 'date' : 'text'}
                  value={formData.default_value}
                  onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional default value"
                />
              )}
            </div>

            {/* Required Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_required"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_required" className="ml-2 text-sm text-gray-700">
                Make this field required
              </label>
            </div>

            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {field ? 'Update Field' : 'Create Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomFieldsTab;
